import { Image, type ImageProps } from "@sidelinesports/fan-design-system";
import { useImpressionTracking } from "../analytics/use-impression-tracking.ts";
import type { ApiUploadedImage } from "../api";

type TrackingOptions =
  | {
      enabled: false;
      location?: string;
    }
  | {
      enabled?: true;
      location: string;
    };

export type UploadedImageProps = {
  image: ApiUploadedImage | null;
  alt: string;
  tracking: TrackingOptions;
} & Omit<ImageProps, "src" | "alt" | "width" | "height">;

export const UploadedImage = ({
  image,
  alt,
  tracking: { enabled: shouldTrackView, location: imageLocation },
  ...props
}: UploadedImageProps) => {
  const setImpressionElement = useImpressionTracking<HTMLImageElement>(
    "IMAGE_VIEW",
    {
      properties: {
        imageId: image?.id,
        src: image?.url,
        location: imageLocation,
      },
      once: true,
      enabled: shouldTrackView,
    },
  );

  if (!image) {
    return null;
  }

  return (
    <Image
      src={image.url}
      onLoad={(event) => {
        if (event.target instanceof HTMLImageElement) {
          setImpressionElement(event.target);
        }
      }}
      alt={alt}
      loading="lazy"
      placeholder={getPlaceholderUrl(image)}
      {...props}
    />
  );
};

function getPlaceholderUrl(image: ApiUploadedImage) {
  if (!image.placeholder || !image.placeholderFormat) {
    return undefined;
  }

  return `data:${image.placeholderFormat};base64,${image.placeholder}`;
}
